/**
 * WelcomePage.tsx — /app/welcome — first-signup onboarding wizard
 *
 * Purpose:    Guided 3-step flow shown right after signup: (1) create your
 *             first monitor (URL + interval), (2) optionally create a status
 *             page, (3) optionally invite a teammate. Steps 2 and 3 are
 *             skippable; finishing lands on /app/monitors.
 * Inputs:     monitorsApi + statusPagesApi from @/lib/saas-client;
 *             teamApi from @/lib/saas-org
 * Outputs:    Created monitor (required), status page + invite (optional)
 * Constraints: Dark-first; WCAG AA (labels, focus-visible, aria-current on
 *              the step list). Team invites may still be rolling out on the
 *              gateway — a coming-online failure is treated as skippable,
 *              never a dead end.
 * SOT:        nsentry SaaS completion spec § G-WEB (onboarding wizard)
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { monitorsApi, statusPagesApi } from '@/lib/saas-client'
import { teamApi } from '@/lib/saas-org'
import { isComingOnline } from '@/lib/sentry-error'

const STEPS = ['Add a monitor', 'Status page', 'Invite your team'] as const

const INTERVALS = [
  { label: 'Every minute', seconds: 60 },
  { label: 'Every 5 minutes', seconds: 300 },
  { label: 'Every 15 minutes', seconds: 900 },
  { label: 'Every 30 minutes', seconds: 1800 },
] as const

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 48)
}

const inputClass =
  'w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500'
const primaryBtn =
  'rounded-lg bg-sky-700 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:cursor-not-allowed disabled:opacity-50'
const ghostBtn =
  'rounded-lg border border-brand-card px-5 py-2.5 text-sm font-medium text-brand-secondary transition-colors hover:bg-brand-surface-2 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400'

export function WelcomePage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Step 1 — first monitor
  const [monitorName, setMonitorName] = useState('')
  const [monitorUrl, setMonitorUrl] = useState('')
  const [intervalSeconds, setIntervalSeconds] = useState<number>(300)

  // Step 2 — optional status page
  const [pageTitle, setPageTitle] = useState('')

  // Step 3 — optional teammate
  const [inviteEmail, setInviteEmail] = useState('')

  function finish() {
    navigate('/app/monitors')
  }

  async function handleCreateMonitor(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!monitorUrl.startsWith('http://') && !monitorUrl.startsWith('https://')) {
      setError('URL must start with http:// or https://')
      return
    }
    let fallbackName = monitorUrl
    try {
      fallbackName = new URL(monitorUrl).hostname || monitorUrl
    } catch {
      setError('That does not look like a valid URL')
      return
    }
    setBusy(true)
    const result = await monitorsApi.create({
      name: monitorName || fallbackName,
      url: monitorUrl,
      intervalSeconds,
    })
    setBusy(false)
    if (!result.ok) {
      setError(result.error.message)
      return
    }
    setStep(1)
  }

  async function handleCreateStatusPage(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!pageTitle.trim()) {
      setError('Give your status page a title')
      return
    }
    setBusy(true)
    const result = await statusPagesApi.create({ title: pageTitle.trim(), slug: slugify(pageTitle) })
    setBusy(false)
    if (!result.ok) {
      setError(result.error.message)
      return
    }
    setError(null)
    setStep(2)
  }

  async function handleInvite(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    const result = await teamApi.invite({ email: inviteEmail.trim(), role: 'member' })
    setBusy(false)
    if (!result.ok) {
      // Invites rolling out on the gateway → not a dead end, just move on.
      if (isComingOnline(result.error)) {
        finish()
        return
      }
      setError(result.error.message)
      return
    }
    finish()
  }

  function skip() {
    setError(null)
    if (step >= 2) finish()
    else setStep(step + 1)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <Link to="/" className="text-2xl font-bold text-sky-700 dark:text-sky-400">
            ɳSentry
          </Link>
          <h1 className="mt-4 text-2xl font-bold text-brand-ink">Welcome — let&apos;s get you monitoring</h1>
        </div>

        {/* Step indicator */}
        <ol className="mb-6 flex items-center justify-center gap-2" aria-label="Onboarding progress">
          {STEPS.map((label, i) => (
            <li
              key={label}
              aria-current={i === step ? 'step' : undefined}
              className={[
                'flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium',
                i === step
                  ? 'bg-sky-50 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300'
                  : i < step
                    ? 'text-green-700 dark:text-green-400'
                    : 'text-brand-muted',
              ].join(' ')}
            >
              <span aria-hidden="true">{i < step ? '✓' : `${i + 1}.`}</span> {label}
            </li>
          ))}
        </ol>

        <div className="rounded-2xl border border-brand-subtle bg-brand-surface p-8 shadow-sm">
          {error && (
            <div className="mb-5 rounded-lg bg-red-50 dark:bg-red-950/40 px-4 py-3" role="alert">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {step === 0 && (
            <form onSubmit={handleCreateMonitor} className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-brand-ink">Add your first monitor</h2>
                <p className="mt-1 text-sm text-brand-muted">
                  We&apos;ll check this URL on the interval you pick and alert you when it goes down.
                </p>
              </div>
              <div>
                <label htmlFor="ob-url" className="mb-1 block text-sm font-medium text-brand-secondary">
                  URL to monitor
                </label>
                <input
                  id="ob-url"
                  type="url"
                  required
                  value={monitorUrl}
                  onChange={(e) => setMonitorUrl(e.target.value)}
                  className={inputClass}
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label htmlFor="ob-name" className="mb-1 block text-sm font-medium text-brand-secondary">
                  Name <span className="font-normal text-brand-muted">(optional — defaults to the hostname)</span>
                </label>
                <input
                  id="ob-name"
                  type="text"
                  value={monitorName}
                  onChange={(e) => setMonitorName(e.target.value)}
                  className={inputClass}
                  placeholder="Marketing site"
                />
              </div>
              <div>
                <label htmlFor="ob-interval" className="mb-1 block text-sm font-medium text-brand-secondary">
                  Check interval
                </label>
                <select
                  id="ob-interval"
                  value={intervalSeconds}
                  onChange={(e) => setIntervalSeconds(Number(e.target.value))}
                  className={inputClass}
                >
                  {INTERVALS.map((opt) => (
                    <option key={opt.seconds} value={opt.seconds}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-brand-muted">Free tier checks run at 5-minute intervals or slower.</p>
              </div>
              <div className="flex items-center justify-between pt-1">
                <button type="button" onClick={skip} className={ghostBtn}>
                  Skip for now
                </button>
                <button type="submit" disabled={busy} className={primaryBtn}>
                  {busy ? 'Creating…' : 'Create monitor'}
                </button>
              </div>
            </form>
          )}

          {step === 1 && (
            <form onSubmit={handleCreateStatusPage} className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-brand-ink">Create a status page <span className="text-sm font-normal text-brand-muted">(optional)</span></h2>
                <p className="mt-1 text-sm text-brand-muted">
                  A public page your users can check during incidents. You can add components and
                  subscribers later under Status Pages.
                </p>
              </div>
              <div>
                <label htmlFor="ob-page-title" className="mb-1 block text-sm font-medium text-brand-secondary">
                  Status page title
                </label>
                <input
                  id="ob-page-title"
                  type="text"
                  value={pageTitle}
                  onChange={(e) => setPageTitle(e.target.value)}
                  className={inputClass}
                  placeholder="Acme status"
                />
                {pageTitle.trim() && (
                  <p className="mt-1 text-xs text-brand-muted">
                    Will be published at /s/{slugify(pageTitle) || 'your-slug'}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between pt-1">
                <button type="button" onClick={skip} className={ghostBtn}>
                  Skip
                </button>
                <button type="submit" disabled={busy} className={primaryBtn}>
                  {busy ? 'Creating…' : 'Create status page'}
                </button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleInvite} className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-brand-ink">Invite your team <span className="text-sm font-normal text-brand-muted">(optional)</span></h2>
                <p className="mt-1 text-sm text-brand-muted">
                  Teammates can manage monitors and incidents with you. Seat limits depend on your
                  plan — manage members any time under Team.
                </p>
              </div>
              <div>
                <label htmlFor="ob-invite" className="mb-1 block text-sm font-medium text-brand-secondary">
                  Teammate&apos;s email
                </label>
                <input
                  id="ob-invite"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className={inputClass}
                  placeholder="teammate@example.com"
                />
              </div>
              <div className="flex items-center justify-between pt-1">
                <button type="button" onClick={skip} className={ghostBtn}>
                  Skip — go to my dashboard
                </button>
                <button type="submit" disabled={busy} className={primaryBtn}>
                  {busy ? 'Sending…' : 'Send invite & finish'}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-brand-muted">
          You can do all of this later —{' '}
          <Link to="/app/monitors" className="text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
            skip straight to the app
          </Link>
        </p>
      </div>
    </div>
  )
}
